import { Injectable } from '@angular/core';
import {
  IMarketContract,
  IMarketContractPost,
  IMarketContractOptions,
} from '@/app/core/interfaces/market-contracts.interface';
import { AppSettings } from '@/app/core/utils/app.settings';
import { HttpService } from '@/app/core/services/http/http.service';
import { ADMIN_SERVICES } from '@/app/core/const/http.const';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class MarketContractsService {
  marketContracts: IMarketContract[];

  constructor(
    private http: HttpService,
    private httpClient: HttpClient,
    private appSetting: AppSettings
  ) {}

  async getAllMarketContracts(): Promise<IMarketContract[]> {
    const url: string = `${this.appSetting.adminURL}/${ADMIN_SERVICES.MARKET_CONTRACTS}`;
    try {
      const response = await firstValueFrom(
        this.httpClient.get<IMarketContract[]>(url)
      );
      this.marketContracts = response || [];
      return this.marketContracts;
    } catch (error) {
      console.error('Error getting market contracts:', error);
      this.marketContracts = [];
      return [];
    }
  }

  async updateLocalMarketContracts(): Promise<IMarketContract[]> {
    return await this.getAllMarketContracts();
  }

  /**
   * GET /options
   * @param entregaTramos - Array de UUIDs de tramos seleccionados. Cuando se envía,
   * el backend devuelve puntoInicioFuentes y puntoInicioPuntos.
   */
  async getMarketContractOptions(entregaTramos?: string[]): Promise<IMarketContractOptions> {
    const params = new URLSearchParams();
    if (entregaTramos?.length) {
      entregaTramos.forEach(uuid => params.append('entregaTramos', uuid));
    }
    const query = params.toString();
    const url = `${this.appSetting.adminURL}/${ADMIN_SERVICES.MARKET_CONTRACTS}/options${query ? `?${query}` : ''}`;
    try {
      return (await this.http.getService(url)) as IMarketContractOptions;
    } catch (error) {
      console.error('Error getting market contract options:', error);
      return {};
    }
  }

  async createUpdateMarketContract(
    data: IMarketContractPost | Partial<IMarketContractPost>,
    uuid?: string
  ) {
    const url: string = `${this.appSetting.adminURL}/${ADMIN_SERVICES.MARKET_CONTRACTS}`;
    try {
      const res = uuid
        ? await this.http.putService(`${url}/${uuid}`, data)
        : await this.http.postService(url, data);
      // Limpiar cache después de crear/actualizar
      this.marketContracts = [];
      return { res, status: true };
    } catch (err) {
      return { err, status: false };
    }
  }

  async deleteMarketContract(uuid: string) {
    const url: string = `${this.appSetting.adminURL}/${ADMIN_SERVICES.MARKET_CONTRACTS}/${uuid}`;
    try {
      await this.http.deleteService(url);
      // Limpiar cache después de eliminar
      this.marketContracts = [];
      return true;
    } catch {
      return false;
    }
  }

  async getMarketContractsByAgente(): Promise<IMarketContract[]> {
    const url: string = `${this.appSetting.adminURL}/${ADMIN_SERVICES.MARKET_CONTRACTS}/by-agente`;
    try {
      const response = await firstValueFrom(
        this.httpClient.get<IMarketContract[]>(url)
      );
      this.marketContracts = response || [];
      return this.marketContracts;
    } catch (error) {
      console.error('Error getting market contracts by agente:', error);
      this.marketContracts = [];
      return [];
    }
  }
}
